// Side-cart drawer — slides in from the trailing edge when the header
// cart icon is clicked, instead of navigating to the full /cart/ page.
//
// Responsibilities:
//   1. Hijack any [data-cart-link] click → open drawer, prevent nav
//   2. Fetch server-rendered drawer body (haegoziya_cart_drawer endpoint)
//   3. Wire qty +/- and remove handlers per row, using the existing
//      haegoziya_cart_update endpoint (no new server code needed)
//   4. Refresh subtotal/total/count after every change
//   5. Sync the header bubble + dispatch a body-level event so other
//      modules (mini-cart fragment listeners) stay coherent
//
// Keeping the rows server-rendered (rather than JSON + JS template) means
// price formatting / Hebrew name translation reuses the same wc_price()
// + woocommerce_cart_item_name path as the full cart page.

(function () {
    const HAEGOZIYA = window.HAEGOZIYA || {};
    const ajaxUrl   = HAEGOZIYA.ajaxUrl   || '/wp-admin/admin-ajax.php';
    const qtyNonce  = HAEGOZIYA.qtyNonce;
    if (!qtyNonce) return;

    const drawer = document.querySelector('[data-cart-drawer]');
    if (!drawer) return;

    const els = {
        panel:        drawer.querySelector('.cart-drawer__panel'),
        content:      drawer.querySelector('[data-cart-drawer-content]'),
        foot:         drawer.querySelector('[data-cart-drawer-foot]'),
        countEls:     document.querySelectorAll('[data-cart-drawer-count], [data-cart-count]'),
        subtotal:     drawer.querySelector('[data-cart-drawer-subtotal]'),
        taxRow:       drawer.querySelector('[data-cart-drawer-tax-row]'),
        tax:          drawer.querySelector('[data-cart-drawer-tax]'),
        taxLabel:     drawer.querySelector('[data-cart-drawer-tax-label]'),
        total:        drawer.querySelector('[data-cart-drawer-total]'),
        cartUrlBtn:   drawer.querySelector('[data-cart-drawer-cart-url]'),
        checkoutBtn:  drawer.querySelector('[data-cart-drawer-checkout-url]'),
        confirm:      drawer.querySelector('[data-cart-drawer-confirm]'),
        confirmText:  drawer.querySelector('[data-cart-drawer-confirm-text]'),
        confirmOk:    drawer.querySelector('[data-cart-drawer-confirm-ok]'),
        confirmCancel:drawer.querySelector('[data-cart-drawer-confirm-cancel]'),
    };

    const CURRENCY = HAEGOZIYA.currency || '₪';
    const fmtMoney = (n) => `${(Math.round(n * 100) / 100).toFixed(2)} ${CURRENCY}`;

    let lastFocusedTrigger = null;
    let isOpen = false;
    let pendingMutation = false;            // gate concurrent qty/remove ops
    let didInitialFetch = false;

    // ── Hijack cart links ───────────────────────────────────────────
    document.addEventListener('click', (e) => {
        const trigger = e.target.closest('[data-cart-link]');
        if (!trigger) return;
        // Let modifier-clicks open /cart/ in a new tab as expected.
        if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
        e.preventDefault();
        lastFocusedTrigger = trigger;
        open();
    });

    // ── Open / close ────────────────────────────────────────────────
    function open() {
        if (isOpen) return;
        isOpen = true;
        drawer.hidden = false;
        // Force a reflow so the entrance transition actually runs.
        // eslint-disable-next-line no-unused-expressions
        drawer.offsetWidth;
        drawer.classList.add('is-open');
        drawer.setAttribute('aria-hidden', 'false');
        document.body.classList.add('cart-drawer-open');

        // Always re-fetch on open — cart state may have changed in
        // another tab, after an add-to-cart, etc.
        refresh();

        // Move focus to the close button for keyboard users.
        const closeBtn = drawer.querySelector('[data-cart-drawer-close]:not(.cart-drawer__backdrop)');
        if (closeBtn) closeBtn.focus({ preventScroll: true });
    }

    function close() {
        if (!isOpen) return;
        isOpen = false;
        drawer.classList.remove('is-open');
        drawer.setAttribute('aria-hidden', 'true');
        document.body.classList.remove('cart-drawer-open');
        // Wait for the slide-out animation before hiding from a11y tree
        const onEnd = () => {
            if (isOpen) return;             // re-opened mid-animation
            drawer.hidden = true;
            drawer.removeEventListener('transitionend', onEnd);
        };
        drawer.addEventListener('transitionend', onEnd);

        if (lastFocusedTrigger) {
            try { lastFocusedTrigger.focus({ preventScroll: true }); } catch (_) {}
            lastFocusedTrigger = null;
        }
    }

    // Close on backdrop / close-button click, on Esc.
    drawer.addEventListener('click', (e) => {
        if (e.target.closest('[data-cart-drawer-close]')) {
            e.preventDefault();
            close();
        }
    });
    document.addEventListener('keydown', (e) => {
        if (e.key !== 'Escape') return;
        if (els.confirm && !els.confirm.hidden) { closeConfirm(); return; }
        if (isOpen) close();
    });

    // External event hook — other modules can dispatch this to pop the
    // drawer open after an add-to-cart, etc. (Also fired by the floating
    // cart FAB below.)
    document.addEventListener('haegoziya:open-cart-drawer', () => open());
    // Allow other modules (e.g. quickview opening from a bundle item) to
    // close the drawer so their higher-priority modal isn't hidden behind it.
    document.addEventListener('haegoziya:close-cart-drawer', () => close());

    // ── Floating cart shortcut (FAB) ───────────────────────────────
    // A small pill button that pops up in the bottom corner when the
    // cart has at least one item. Click → opens this same drawer.
    // Stays in sync with the header cart bubble by watching the
    // [data-cart-count] node — every cart-mutation path already updates
    // that node, so the FAB rides on the existing fragment refresh
    // pipeline without needing its own events.
    (function initCartFab() {
        const fab = document.querySelector('[data-cart-fab]');
        if (!fab) return;

        // Don't show on /cart/ or /checkout/ — server already marked it.
        const hideOnCartPages = fab.dataset.hideOnCartPages === '1';
        if (hideOnCartPages) { fab.hidden = true; return; }

        const countBadge   = fab.querySelector('[data-cart-fab-count]');
        const countText    = fab.querySelector('[data-cart-fab-count-text]');
        const headerBubble = document.querySelector('[data-cart-count]');

        // Click → tell the drawer to open. We dispatch the event rather
        // than calling open() directly so the listener above stays the
        // single entry point (matches the documented integration hook).
        fab.addEventListener('click', (e) => {
            e.preventDefault();
            document.dispatchEvent(new CustomEvent('haegoziya:open-cart-drawer'));
        });

        // Always-visible mode: the FAB stays on every page (except
        // /cart/ and /checkout/, gated server-side). We only swap
        // between an "empty" skin and the populated skin via .is-empty.
        const syncFromCount = (raw) => {
            const n = parseInt(raw, 10) || 0;
            if (countBadge) countBadge.textContent = String(n);
            if (countText)  countText.textContent  = String(n);

            // Empty-state class swap — CSS hides the badge + flips the
            // hint label when this is on.
            fab.classList.toggle('is-empty', n === 0);

            // Trigger the entrance animation once. Subsequent count
            // changes don't re-animate the wrapper — just the bump.
            if (!fab.classList.contains('is-visible')) {
                requestAnimationFrame(() => fab.classList.add('is-visible'));
            }

            // Pulse only on a real increase so the initial render is calm.
            const prev = parseInt(fab.dataset.lastCount || '0', 10);
            if (n > prev && prev >= 0 && n > 0 && fab.dataset.lastCount !== undefined && prev !== n) {
                // Skip the very first sync (lastCount unset → undefined → '0').
                if (fab.dataset.lastCount !== '') {
                    fab.classList.remove('is-bumping');
                    void fab.offsetWidth;
                    fab.classList.add('is-bumping');
                }
            }
            fab.dataset.lastCount = String(n);
        };

        // Initial sync — prefer the header bubble (authoritative for
        // logged-in users), fall back to the FAB's own server-rendered
        // count. The pill renders either way (always-visible mode);
        // the only thing this decides is the initial number on the
        // badge + the empty-state class.
        const initialFromHeader = headerBubble ? parseInt(headerBubble.textContent, 10) : NaN;
        const initialFromFab    = parseInt((countBadge && countBadge.textContent) || '0', 10) || 0;
        const initialCount      = Number.isFinite(initialFromHeader)
            ? initialFromHeader
            : initialFromFab;
        // Mark lastCount as known so the first sync doesn't fire a bump.
        fab.dataset.lastCount = '';
        syncFromCount(initialCount);

        if (headerBubble) {
            // Watch the header bubble for changes. Every cart mutation
            // (add, remove, drawer qty change, page-level cart update)
            // updates that node, so this single observer keeps the FAB
            // in sync without needing its own event bus.
            new MutationObserver(() => syncFromCount(headerBubble.textContent))
                .observe(headerBubble, { childList: true, characterData: true, subtree: true });
        }
    })();

    // ── Fetch + render ──────────────────────────────────────────────
    async function refresh() {
        const body = new URLSearchParams();
        body.set('action', 'haegoziya_cart_drawer');
        body.set('nonce', qtyNonce);

        // Show loading state on first open OR when re-fetching empty.
        if (!didInitialFetch) {
            els.foot.hidden = true;
        }

        try {
            const res = await fetch(ajaxUrl, {
                method: 'POST',
                body,
                credentials: 'same-origin',
                headers: { 'X-Requested-With': 'XMLHttpRequest' },
                cache: 'no-store',
            });
            if (!res.ok) throw new Error('drawer HTTP ' + res.status);
            const json = await res.json();
            if (!json || !json.success || !json.data) throw new Error('drawer payload invalid');
            apply(json.data);
            didInitialFetch = true;
        } catch (err) {
            els.content.innerHTML =
                '<p class="cart-drawer__error">' +
                'Could not load your cart. Please refresh the page.' +
                '</p>';
        }
    }

    function apply(data) {
        // Replace items markup + bind row handlers
        els.content.innerHTML = data.html || '';
        bindRows();

        // Update header bubble + drawer count
        const count = data.count | 0;
        els.countEls.forEach((el) => { el.textContent = String(count); });

        // Totals + buttons — hidden when cart is empty
        if (data.is_empty) {
            els.foot.hidden = true;
        } else {
            els.foot.hidden = false;
            if (els.subtotal) els.subtotal.innerHTML = data.subtotal || '';
            if (els.total)    els.total.innerHTML    = data.total    || '';
            // VAT line — explains the subtotal → total jump (prices incl. tax).
            if (els.taxRow) {
                if (data.has_tax) {
                    els.taxRow.hidden = false;
                    if (els.tax)      els.tax.innerHTML        = data.tax || '';
                    if (els.taxLabel) els.taxLabel.textContent = data.tax_label || 'VAT';
                } else {
                    els.taxRow.hidden = true;
                }
            }
        }
        if (els.cartUrlBtn  && data.cart_url)     els.cartUrlBtn.href  = data.cart_url;
        if (els.checkoutBtn && data.checkout_url) els.checkoutBtn.href = data.checkout_url;

        // Push Woo's fragment refresh so any other listeners (header
        // bubble, sticky cart icons) re-paint without manual work.
        if (data.fragments) {
            for (const [selector, html] of Object.entries(data.fragments)) {
                document.querySelectorAll(selector).forEach((el) => { el.outerHTML = html; });
            }
        }
        if (window.jQuery) {
            window.jQuery(document.body).trigger('wc_fragment_refresh');
        }
    }

    // ── Per-row handlers ────────────────────────────────────────────
    function bindRows() {
        els.content.querySelectorAll('[data-drawer-row]').forEach((row) => {
            const key      = row.dataset.cartKey;
            const minus    = row.querySelector('[data-drawer-qty-minus]');
            const plus     = row.querySelector('[data-drawer-qty-plus]');
            const removeEl = row.querySelector('[data-drawer-remove]');
            const countEl  = row.querySelector('[data-drawer-qty-count]');
            if (!key || !countEl) return;

            const current = () => parseInt(countEl.textContent, 10) || 0;
            const bundleId = row.dataset.bundleId ? parseInt(row.dataset.bundleId, 10) : 0;

            if (plus) plus.addEventListener('click', (e) => {
                e.preventDefault();
                updateRow(row, key, current() + 1);
            });
            if (minus) minus.addEventListener('click', (e) => {
                e.preventDefault();
                const next = Math.max(0, current() - 1);
                // Reducing a bundle member to 0 breaks the bundle — confirm first.
                if (bundleId && next === 0) { confirmBreakBundle(bundleId, key, row); return; }
                updateRow(row, key, next);
            });
            if (removeEl) removeEl.addEventListener('click', (e) => {
                e.preventDefault();
                if (bundleId) { confirmBreakBundle(bundleId, key, row); return; }
                updateRow(row, key, 0);
            });
        });
    }

    // ── Bundle-break confirmation ───────────────────────────────────
    // Removing a member of a bundle dissolves the bundle discount: the
    // remaining items stay in the cart but go back to full price. Warn the
    // shopper (with the price increase) before doing it.
    function confirmBreakBundle(bundleId, removeKey, row) {
        // Sum the discount currently on the OTHER members of this bundle —
        // that's how much the cart rises when they revert to full price.
        let increase = 0;
        els.content
            .querySelectorAll('[data-drawer-row][data-bundle-id="' + bundleId + '"]')
            .forEach((r) => {
                if (r === row) return; // the one being removed leaves the cart
                const qty  = parseInt(r.querySelector('[data-drawer-qty-count]')?.textContent, 10) || 0;
                const base = parseFloat(r.dataset.unitBase || '0') || 0; // its own normal/sale price
                const now  = parseFloat(r.dataset.unitNow  || '0') || 0; // current bundle price
                increase += Math.max(0, (base - now)) * qty;
            });

        const others = els.content.querySelectorAll('[data-drawer-row][data-bundle-id="' + bundleId + '"]').length - 1;

        if (els.confirmText) {
            let msg;
            if (others > 0 && increase > 0.005) {
                msg = `Removing this item breaks the bundle. The other ${others} item${others > 1 ? 's' : ''} go back to their full price — about ${fmtMoney(increase)} more.`;
            } else if (others > 0) {
                msg = `Removing this item breaks the bundle. The other ${others} item${others > 1 ? 's' : ''} go back to their regular price.`;
            } else {
                msg = 'Removing this item dissolves the bundle.';
            }
            els.confirmText.textContent = msg;
        }

        openConfirm(() => breakBundle(bundleId, removeKey));
    }

    let confirmHandler = null;
    function openConfirm(onOk) {
        confirmHandler = onOk;
        if (!els.confirm) { if (onOk) onOk(); return; }
        els.confirm.hidden = false;
        requestAnimationFrame(() => els.confirm.classList.add('is-open'));
        if (els.confirmOk) els.confirmOk.focus({ preventScroll: true });
    }
    function closeConfirm() {
        confirmHandler = null;
        if (!els.confirm) return;
        els.confirm.classList.remove('is-open');
        els.confirm.hidden = true;
    }
    if (els.confirmOk) els.confirmOk.addEventListener('click', (e) => {
        e.preventDefault();
        const fn = confirmHandler;
        closeConfirm();
        if (fn) fn();
    });
    if (els.confirmCancel) els.confirmCancel.addEventListener('click', (e) => {
        e.preventDefault();
        closeConfirm();
    });

    async function breakBundle(bundleId, removeKey) {
        if (pendingMutation) return;
        pendingMutation = true;
        try {
            const body = new URLSearchParams();
            body.set('action', 'haegoziya_break_bundle');
            body.set('nonce', qtyNonce);
            body.set('bundle_id', String(bundleId));
            body.set('key', removeKey || '');
            const res = await fetch(ajaxUrl, {
                method: 'POST', body, credentials: 'same-origin', cache: 'no-store',
            });
            if (!res.ok) throw new Error('break_bundle HTTP ' + res.status);
            const json = await res.json();
            if (!json || !json.success) throw new Error('break_bundle rejected');
            await refresh();
            if (window.jQuery) window.jQuery(document.body).trigger('wc_fragment_refresh');
        } catch (err) {
            // leave the cart as-is on failure
        } finally {
            pendingMutation = false;
        }
    }

    async function updateRow(row, key, quantity) {
        if (pendingMutation) return;
        pendingMutation = true;
        row.classList.add('is-pending');

        try {
            const body = new URLSearchParams();
            body.set('action', 'haegoziya_cart_update');
            body.set('nonce', qtyNonce);
            body.set('key', key);
            body.set('quantity', String(quantity));

            const res = await fetch(ajaxUrl, {
                method: 'POST',
                body,
                credentials: 'same-origin',
                cache: 'no-store',
            });
            if (!res.ok) throw new Error('cart_update HTTP ' + res.status);
            const json = await res.json();
            if (!json || !json.success) throw new Error('cart_update rejected');

            // Easiest path to a consistent UI: re-fetch the drawer body.
            // The cart_update payload has line_subtotal + totals_html but
            // they target the full cart page's structure, not ours.
            await refresh();

            // Tell quick-add cards on the underlying page their counts
            // may have changed. They re-hydrate via their own AJAX, so
            // a fragment-refresh trigger is enough.
            if (window.jQuery) {
                window.jQuery(document.body).trigger('wc_fragment_refresh');
            }
        } catch (err) {
            row.classList.remove('is-pending');
        } finally {
            pendingMutation = false;
        }
    }
})();
